import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories } from './CategoryDropdown';

const navLinks = [
  { key: 'nav_home', to: '/' },
  { key: 'nav_spreadsheet', to: '/spreadsheet' },
  { key: 'nav_categories', to: '/categories' },
  { key: 'nav_guides', to: '/guides' },
  { key: 'nav_reviews', to: '/reviews' },
  { key: 'nav_shipping', to: '/shipping' },
  { key: 'nav_safety', to: '/safety' },
];

const languages = [
  { code: 'pt', label: 'Portugu\u00eas' },
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation('home');
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLang = (lng: string) => {
    i18n.changeLanguage(lng);
    setLangOpen(false);
  };

  const isHome = location.pathname === '/';
  const transparent = !isScrolled && isHome;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        transparent
          ? 'bg-black/30 backdrop-blur-sm border-b border-white/10'
          : 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className={`text-lg md:text-xl font-bold tracking-tight ${transparent ? 'text-white' : 'text-foreground-900'}`}>
              CSSBuy<span className="text-primary-500">Spreadsheet</span>
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => {
              const active = location.pathname === link.to;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    transparent
                      ? active
                        ? 'text-white bg-white/15'
                        : 'text-white/85 hover:text-white hover:bg-white/10'
                      : active
                        ? 'text-primary-700 bg-primary-50'
                        : 'text-foreground-700 hover:text-primary-700 hover:bg-background-100'
                  }`}
                >
                  {t(link.key)}
                </Link>
              );
            })}
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/guides"
              className={`hidden lg:inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors ${
                transparent
                  ? 'bg-white/90 text-foreground-900 hover:bg-white'
                  : 'bg-accent-500 text-background-50 hover:bg-accent-600'
              }`}
            >
              <i className="ri-shopping-cart-2-line"></i>
              {t('nav_shopping_guide')}
            </Link>

            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  transparent ? 'text-white/85 hover:bg-white/10' : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                <i className="ri-global-line text-base"></i>
                <span className="hidden sm:inline">{i18n.language.toUpperCase()}</span>
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-background-50 border border-background-200 rounded-xl shadow-lg overflow-hidden z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLang(lang.code)}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-background-100 transition-colors ${
                        i18n.language === lang.code ? 'text-primary-700 bg-primary-50/50 font-medium' : 'text-foreground-700'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`lg:hidden p-2 rounded-lg transition-colors ${
                transparent ? 'text-white hover:bg-white/10' : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-background-50/98 backdrop-blur-md border-t border-background-200/60 px-4 pb-6 max-h-[80vh] overflow-y-auto">
          <div className="pt-3 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className={`block px-4 py-3 rounded-lg text-sm font-medium ${
                  location.pathname === link.to
                    ? 'text-primary-700 bg-primary-50'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                {t(link.key)}
              </Link>
            ))}
          </div>

          <Link
            to="/guides"
            onClick={() => setMobileOpen(false)}
            className="mt-3 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold bg-accent-500 text-background-50"
          >
            <i className="ri-shopping-cart-2-line"></i>
            {t('nav_shopping_guide')}
          </Link>

          <div className="mt-5">
            <h4 className="px-4 text-xs font-semibold uppercase tracking-wider text-foreground-500 mb-2">
              {t('mobile_categories_title')}
            </h4>
            <div className="grid grid-cols-2 gap-1.5">
              {categories.map((cat) => (
                <a
                  key={cat.url}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-700 transition-colors"
                >
                  <i className="ri-shopping-bag-3-line text-foreground-400 text-base"></i>
                  <span className="truncate">{cat.label}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}